import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { Trash2 as TrashIcon } from "lucide-react";
import { PauseIcon, PlayIcon } from "../../../../assets/icon";
import { AnimatedRecordTimer } from "../../animated-record-timer/animated-record-timer";
import { AudioWaveformVisualizer } from "../../whatsapp-audio-recorder/whatsapp-audio-recorder";
import {
  cancelHintStyle,
  lockedBarStyle,
  lockedCancelTextStyle,
  lockedTimerGroupStyle,
  pausedPlayPillStyle,
  pausedPlayTimeStyle,
  pausedPreviewBarStyle,
  pausedWaveformWrapStyle,
  recordingBarStyle,
  recordingDotStyle,
  recordingOverlayStyle,
  trashButtonStyle,
} from "./styles";
interface TelegramRecordingOverlayProps {
  recordTimeMs: number;
  clockTime: string;
  cancelProgress: number;
  isLocked: boolean;
  isPaused: boolean;
  isCancelling: boolean;
  dragOffsetX: number;
  pausedPreviewUrl?: string | null;
  durationSeconds?: number;
  onCancelLocked?: () => void;
}

const DELETE_ANIMATION_MS = 480;

const formatPlaybackTime = (seconds: number): string => {
  const safe = Number.isFinite(seconds) && seconds >= 0 ? seconds : 0;
  const mins = Math.floor(safe / 60);
  const secs = Math.floor(safe % 60);
  return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
};

export const TelegramRecordingOverlay: React.FC<
  TelegramRecordingOverlayProps
> = ({
  recordTimeMs,
  clockTime,
  cancelProgress,
  isLocked,
  isPaused,
  isCancelling,
  dragOffsetX,
  pausedPreviewUrl,
  durationSeconds,
  onCancelLocked,
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const deleteTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackTime, setPlaybackTime] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    setIsPlaying(false);
    setPlaybackTime(0);
    setIsDeleting(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, [pausedPreviewUrl, isPaused]);

  useEffect(() => {
    return () => {
      if (deleteTimeoutRef.current) {
        clearTimeout(deleteTimeoutRef.current);
      }
    };
  }, []);

  const togglePlayback = useCallback(async () => {
    if (!audioRef.current || !pausedPreviewUrl) return;
    try {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        await audioRef.current.play();
        setIsPlaying(true);
      }
    } catch (error) {
      console.error("Paused preview playback failed:", error);
      setIsPlaying(false);
    }
  }, [isPlaying, pausedPreviewUrl]);

  const handleDeleteClick = useCallback(() => {
    if (isDeleting) return;
    setIsDeleting(true);
    if (deleteTimeoutRef.current) {
      clearTimeout(deleteTimeoutRef.current);
    }
    deleteTimeoutRef.current = setTimeout(() => {
      onCancelLocked?.();
    }, DELETE_ANIMATION_MS);
  }, [isDeleting, onCancelLocked]);

  if (isLocked && isPaused) {
    return (
      <Box sx={recordingOverlayStyle()}>
        <Box sx={pausedPreviewBarStyle}>
          <IconButton
            type="button"
            aria-label="Delete recording"
            onClick={handleDeleteClick}
            disabled={isDeleting}
            sx={trashButtonStyle(isDeleting)}
          >
            <TrashIcon size={18} strokeWidth={2} />
          </IconButton>

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              height: "100%",
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "opacity 0.35s ease 0.12s",
              opacity: isDeleting ? 0 : 1,
            }}
          >
            {pausedPreviewUrl ? (
              <>
                <audio
                  ref={audioRef}
                  src={pausedPreviewUrl}
                  preload="metadata"
                  playsInline
                  onTimeUpdate={() => {
                    setPlaybackTime(audioRef.current?.currentTime || 0);
                  }}
                  onEnded={() => {
                    setIsPlaying(false);
                    setPlaybackTime(0);
                  }}
                  style={{ display: "none" }}
                />
                <Box sx={pausedWaveformWrapStyle}>
                  <AudioWaveformVisualizer
                    isRecording={false}
                    isPlaying={isPlaying}
                    audioUrl={pausedPreviewUrl}
                    audioRef={audioRef}
                    waveHeight={22}
                    durationHint={durationSeconds}
                  />
                </Box>
              </>
            ) : null}

            <Box
              component="button"
              type="button"
              onClick={() => {
                void togglePlayback();
              }}
              sx={pausedPlayPillStyle}
              aria-label={isPlaying ? "Pause preview" : "Play preview"}
            >
              {isPlaying ? <PauseIcon size={12} /> : <PlayIcon size={12} />}
              <Typography component="span" sx={pausedPlayTimeStyle}>
                {isPlaying || playbackTime > 0
                  ? formatPlaybackTime(playbackTime)
                  : clockTime}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  if (isLocked) {
    return (
      <Box sx={recordingOverlayStyle()}>
        <Box sx={lockedBarStyle}>
          <Box sx={lockedTimerGroupStyle}>
            <Box sx={recordingDotStyle} />
            <AnimatedRecordTimer timeMs={recordTimeMs} />
          </Box>
          <Typography
            component="button"
            type="button"
            onClick={onCancelLocked}
            sx={lockedCancelTextStyle}
          >
            CANCEL
          </Typography>
        </Box>
      </Box>
    );
  }

  const hintOpacity = Math.max(0.35, 1 - cancelProgress * 0.7);
  const barShift = Math.min(0, dragOffsetX);
  const holdTimerColor = isCancelling ? "#B91C1C" : "#1C1C1F";

  return (
    <Box sx={recordingOverlayStyle()}>
      <Box
        sx={recordingBarStyle(isCancelling)}
        style={{ transform: `translateX(${barShift}px)` }}
      >
        <Box sx={recordingDotStyle} />
        <AnimatedRecordTimer
          timeMs={recordTimeMs}
          sx={{ color: holdTimerColor }}
        />
        <Typography
          component="span"
          sx={cancelHintStyle(hintOpacity, isCancelling)}
        >
          {"< Slide to cancel"}
        </Typography>
      </Box>
    </Box>
  );
};

export default TelegramRecordingOverlay;
